/**
 * Reading an apartment off a delivery address, wherever it ended up.
 *
 * The apartment is a first-class part of the address in the countries we operate in — a driver
 * needs it more than the postal code — but it does not always arrive in `apartment`:
 *
 *  - WhatsApp Flow checkout sends it structured, in `apartment`.
 *  - The web checkout's fulfilment DTO has no structured address at all (one free-text line), so it
 *    folds the apartment into the driver's note, prefixed: `Apto 223 · Portón de garage`.
 *
 * Both shapes are already out there on historical orders, so the merchant surfaces normalise at
 * READ time: lift the apartment out of the note, and keep only the real comment behind it. Mirrors
 * `menteo-loyalty-app`'s `splitApartmentFromInstructions`, which does the same on re-edit.
 */

/** The prefix the web checkout writes, in either language, plus the variants people type by hand. */
const APARTMENT_NOTE_RE = /^\s*(?:apto|apt|dpto|depto|depa)\b\.?\s*:?\s*([^·]+?)\s*(?:·\s*(.*))?$/i;

/**
 * Is this really an apartment identifier, and not a note that merely opens with the word?
 *
 * "Apto 223" is one; "Apto para silla de ruedas" is a comment, and lifting it into the apartment
 * field would be worse than leaving it where it is. A real one is short and carries a digit —
 * except for the letter-only ones ("PB", "A"), which are short enough to be unambiguous.
 */
function looksLikeApartment(value: string): boolean {
  if (value.length > 16) return false;
  return /\d/.test(value) || value.length <= 3;
}

export interface AddressApartmentFields {
  apartment?: string;
  instructions?: string;
}

const clean = (value: string | undefined): string | undefined => {
  const trimmed = value?.trim();
  return trimmed ? trimmed : undefined;
};

/**
 * The address's apartment and the comment for the driver, separated.
 *
 * A structured `apartment` always wins and leaves the note untouched; only when it is missing is the
 * note parsed for a folded-in apartment.
 */
export function splitApartmentFromInstructions(address: AddressApartmentFields): {
  apartment?: string;
  instructions?: string;
} {
  const apartment = clean(address.apartment);
  const instructions = clean(address.instructions);
  if (!instructions) return { apartment, instructions };

  const match = APARTMENT_NOTE_RE.exec(instructions);
  const candidate = clean(match?.[1]);
  const rest = clean(match?.[2]);

  // A structured apartment the note repeats (a saved address reused on a new order): keep the field
  // and drop the echo, so the same "Apto 223" is not shown twice.
  if (apartment) {
    const echoed = !!candidate && candidate.toLowerCase() === apartment.toLowerCase();
    return { apartment, instructions: echoed ? rest : instructions };
  }

  if (!candidate || !looksLikeApartment(candidate)) return { apartment: undefined, instructions };
  return { apartment: candidate, instructions: rest };
}
